"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface FitTextProps {
  children: string;
  className?: string;
  as?: "h1" | "h2" | "div" | "span";
  /**
   * Approximate width of the text divided by its font-size (measured with the
   * display font). Used to render a correct size on the server and before
   * hydration; the ResizeObserver refines it afterwards.
   */
  ratio?: number;
  /** Horizontal padding of the page in px (used by the SSR fallback). */
  gutter?: number;
}

/**
 * Scales a single line of text so it spans exactly the width of its container.
 * Mirrors the edge-to-edge wordmarks used in the TERA VOLTA layout.
 */
const FitText: React.FC<FitTextProps> = ({
  children,
  className,
  as = "div",
  ratio = 9.3,
  gutter = 64,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState<string>(
    `calc((100vw - ${gutter}px) / ${ratio})`
  );

  useEffect(() => {
    const wrap = wrapRef.current;
    const text = textRef.current;
    if (!wrap || !text) return;

    const fit = () => {
      const base = 100;
      text.style.fontSize = `${base}px`;
      const textWidth = text.scrollWidth || 1;
      text.style.fontSize = "";
      const next = (base * wrap.clientWidth) / textWidth;
      if (Number.isFinite(next) && next > 0) setFontSize(`${next}px`);
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    document.fonts?.ready?.then(fit).catch(() => {});
    document.fonts
      ?.load('900 100px "Akira Expanded"')
      .then(fit)
      .catch(() => {});
    // Late-loading assets elsewhere on the page (e.g. the map section's
    // scripts) can shift the scrollbar/layout after fonts report ready;
    // re-check once everything has settled.
    window.addEventListener("load", fit);
    const settleTimer = setTimeout(fit, 500);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", fit);
      clearTimeout(settleTimer);
    };
  }, [children]);

  const Tag = as;

  return (
    <div ref={wrapRef} className="w-full overflow-hidden">
      <Tag
        className={cn("mes-display block whitespace-nowrap", className)}
        style={{ fontSize }}
      >
        {/* Own em tracking: inherited from the tag it would be fixed px and skew the 100px measurement. */}
        <span ref={textRef} className="inline-block whitespace-nowrap tracking-[-0.01em]">
          {children}
        </span>
      </Tag>
    </div>
  );
};

export default FitText;
